import { useContext } from "react";
import { HostPortalContext } from "@/contexts/portalBranding";

/**
 * Shown when the hostname is portal-shaped but no brand holds it — a typo, a
 * brand whose white-label has ended, or a stale link.
 *
 * Deliberately says nothing about Reveshare beyond the fallback link. A visitor
 * here followed a link they believed belonged to one brand; serving them the
 * whole marketplace would be both confusing and a white-label leak.
 *
 * The most common real visitor is an affiliate of a brand that used to be
 * live here — they bookmarked the portal. Their account did not go anywhere:
 * origin never changes, only the lens did, so the copy sends them to the
 * marketplace with the same login rather than leaving them at a dead end.
 */
export function UnknownBrandPage() {
  const { unknownSlug } = useContext(HostPortalContext);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <p className="font-mono text-sm tracking-widest text-muted-foreground">
          404
        </p>
        <h1 className="mt-3 text-2xl font-semibold text-foreground">
          This portal isn’t available here
        </h1>
        <p className="mt-3 text-muted-foreground">
          {unknownSlug ? (
            <>
              No brand is currently using{" "}
              <span className="font-mono text-foreground">{unknownSlug}</span>{" "}
              as its portal address.
            </>
          ) : (
            <>No brand is currently using this portal address.</>
          )}{" "}
          Check the link you followed, or ask the brand for a current one.
        </p>
        <p className="mt-3 text-muted-foreground">
          Already an affiliate of this brand? Your account and login haven’t
          changed — sign in at Reveshare and you’ll find the same programs,
          earnings and payouts there.
        </p>
        <a
          href="https://reveshare.com/auth/login"
          className="mt-8 inline-block rounded-lg border border-border px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          Sign in at Reveshare
        </a>
      </div>
    </main>
  );
}

export default UnknownBrandPage;
